import { Link } from "@tanstack/react-router";
import type { AgentRole } from "@paperclip-cloud/shared";
import type { Device } from "../api/queries/devices";
import { CapabilityBadges } from "./CapabilityBadges";
import { StatusDot, deviceTone } from "./StatusDot";
import { formatRelative } from "../lib/time";

export interface DeviceCardProps {
  device: Device;
  /** Optional running step instruction snippet */
  currentStepLabel?: string | null;
  /** Roles this device hosts. Renders a tiny pill per role next to the name. */
  hostedRoles?: AgentRole[];
}

const ROLE_BADGE: Record<AgentRole, string> = {
  ceo: "CEO",
  ops: "Ops",
};

export function DeviceCard({ device, currentStepLabel, hostedRoles }: DeviceCardProps) {
  const tone = deviceTone(device);
  return (
    <Link
      to="/devices/$id"
      params={{ id: device.id }}
      className="card block p-3 transition-[border-color,box-shadow] duration-[160ms] ease-standard hover:shadow-e2 hover:border-border-strong focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
      aria-label={`Device ${device.name}, ${device.online ? "online" : "offline"}`}
    >
      <div className="flex items-start gap-3">
        <div className="mt-1">
          <StatusDot tone={tone} pulse={tone === "running"} size="lg" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-baseline justify-between gap-2">
            <div className="flex min-w-0 items-baseline gap-2">
              <h3 className="truncate text-[14px] font-semibold tracking-[-0.005em] text-primary">
                {device.name}
              </h3>
              {hostedRoles?.map((r) => (
                <span key={r} className="badge badge--accent shrink-0">
                  {ROLE_BADGE[r]}
                </span>
              ))}
            </div>
            <span className="font-mono text-[11px] text-tertiary shrink-0">
              {device.online
                ? "online"
                : device.last_seen_at
                  ? formatRelative(device.last_seen_at)
                  : "never seen"}
            </span>
          </div>
          <div className="truncate text-[12px] text-tertiary">{device.hostname}</div>
          <div className="mt-2">
            <CapabilityBadges caps={device.capabilities} />
          </div>
          {currentStepLabel && (
            <div className="mt-2 truncate font-mono text-[12px] text-secondary">
              <span className="text-tertiary mr-1">▸</span>
              {currentStepLabel}
            </div>
          )}
          {device.pairing_state === "revoked" && (
            <div className="mt-2 text-[11px] font-medium text-status-error">
              revoked
            </div>
          )}
        </div>
      </div>
    </Link>
  );
}
